<script setup lang="ts">
import { clickoutside as vClickOutside } from '@/common/directives/clickoutside';
import ToggleButtonGroup from '@/common/components/molecules/toggle/ToggleButtonGroup.vue';
import PopupListElement from '../popupListElement/PopupListElement.vue';
import MyInfoPopupProfileArea from './MyInfoPopupProfileArea.vue';
import MyInfoPopupGroupList from './MyInfoPopupGroupList.vue';
import MyInfoPopupLanguageList from './MyInfoPopupLanguageList.vue';
import { type Props, type Emit, setup } from './myInfoPopup.setup';
import PopupLayout from '../PopupLayout.vue';

const props = defineProps<Props>();
const emit = defineEmits<Emit>();

const {
  t,

  mainMenuRef,
  subMenuRef,
  subMenuPopupStyle,

  userProfile,
  languageInfo,
  themeInfo,
  groupItems,
  selectedGroup,

  openedSubPanel,

  POPUP_LIST_BUTTONS,
  LANGUAGE_LIST,
  THEME_OPTIONS,

  onClickOutside,
  onChangeGroup,
  onChangeLanguage,
  onChangeTheme,
  onLeavePopup,
  onLogout,
} = setup(props, emit);
</script>

<template>
  <popup-layout
    ref="mainMenuRef"
    v-click-outside="onClickOutside"
    class="my-info-popup"
    @mouseleave="onLeavePopup"
  >
    <template #header>
      <my-info-popup-profile-area :user-profile="userProfile" />
    </template>
    <template #list>
      <popup-list-element
        v-for="buttonItem in POPUP_LIST_BUTTONS"
        :key="buttonItem.key"
        class="my-info-item"
        :icon="buttonItem.icon"
        :text="t(buttonItem.textKey)"
        :selected="buttonItem.key === openedSubPanel"
        :disabled="buttonItem.disabled"
        @click="buttonItem.onClick"
      >
        <template v-if="buttonItem.key === 'theme'">
          <toggle-button-group
            :model-value="themeInfo"
            type="round"
            :options="THEME_OPTIONS"
            @update:model-value="onChangeTheme"
          />
        </template>
        <template v-if="buttonItem.key === 'language'">
          <span class="my-info-popup__selected-value">
            {{ languageInfo }}
          </span>
          <ev-icon icon="icon-arrow-right" />
        </template>
        <template v-if="buttonItem.key === 'group'">
          <span class="my-info-popup__selected-value">
            {{ selectedGroup?.name ?? '' }}
          </span>
          <ev-icon
            v-if="!buttonItem.disabled"
            icon="icon-arrow-right"
          />
        </template>
      </popup-list-element>

      <stack-separator />
      <popup-list-element
        class="logout-button"
        @click="onLogout"
      >
        <template #left-side>
          <span class="logout-text">{{ t('WORD.GNB.PROFILE_LOGOUT') }}</span>
        </template>
      </popup-list-element>
    </template>
    <template #addition>
      <my-info-popup-group-list
        v-if="openedSubPanel === 'group'"
        ref="subMenuRef"
        class="shown"
        :group-list="groupItems"
        :selected-group="selectedGroup"
        :style="subMenuPopupStyle"
        @click-group-item="onChangeGroup"
      />
      <my-info-popup-language-list
        v-if="openedSubPanel === 'language'"
        ref="subMenuRef"
        class="shown"
        :language-list="LANGUAGE_LIST"
        :selected-language="languageInfo"
        :style="subMenuPopupStyle"
        @click-language-item="onChangeLanguage"
      />
    </template>
  </popup-layout>
</template>

<style lang="scss">
.my-info-popup {
  --popup-wrapper-padding: calc(4px * 2);
  --popup-width: 280px;

  z-index: 9;
  &__selected-value {
    flex: 1;
    font-size: 12px;
    line-height: 14px;
    color: var(--color-blue-05-05);
    text-align: right;

    @include shortening();

    + i {
      width: 16px;
      min-width: 16px;
      height: 16px;
      min-height: 16px;
    }
  }

  .base-menu-popup__inner {
    padding: 62px 6px 6px;
  }

  .base-menu-popup__bottom {
    .popup-list-element {
      padding: 0 12px;
    }
    .popup-list-element__inner {
      .left-side {
        flex: none;
      }

      .right-side {
        flex: 1;
      }
    }
  }

  .ev-radio-group {
    width: 88px;
    height: 24px;
  }

  .separator {
    margin: 4px 0;
    border-color: var(--color-gray-05-03);
  }

  .module-version {
    height: 34px;
  }

  .logout-button {
    .logout-text {
      font-size: 14px;
      font-weight: 500;
      line-height: 120%;
      color: var(--color-red-05-05);
      text-align: start;

      @include shortening();
    }
  }
}
</style>
